import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'

/** A real switch: the whole row is the button, so the hit target is the row. */
export function PanelSwitch({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string
  hint?: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <button type="button" role="switch" aria-checked={checked} className="sp-row sp-switch-row" disabled={disabled} onClick={() => onChange(!checked)}>
      <span className="sp-text">
        <span className="sp-label">{label}</span>
        {hint ? <span className="sp-hint">{hint}</span> : null}
      </span>
      <span className="sp-switch" aria-hidden />
    </button>
  )
}
/**
 * Segmented radio group. The thumb is one element moved with a transform (CSS var --i), so
 * nothing animates per frame in JS; arrow keys move the choice and the focus follows.
 */
export function PanelSegmented<T extends string | number>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  hint?: ReactNode
}) {
  const id = useId()
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const index = Math.max(0, options.findIndex((o) => o.value === value))
  const move = (e: KeyboardEvent, delta: number) => {
    e.preventDefault()
    const next = (index + delta + options.length) % options.length
    onChange(options[next].value)
    refs.current[next]?.focus()
  }
  return (
    <div className="sp-row">
      <div className="sp-text">
        <span id={id} className="sp-label">
          {label}
        </span>
        {hint ? <span className="sp-hint">{hint}</span> : null}
      </div>
      <div className="sp-control">
        <div className="segmented sp-seg" role="radiogroup" aria-labelledby={id} style={{ ['--n' as string]: options.length, ['--i' as string]: index }}>
          <span className="seg-thumb" aria-hidden />
          {options.map((o, i) => (
            <button
              key={String(o.value)}
              ref={(el) => {
                refs.current[i] = el
              }}
              type="button"
              role="radio"
              aria-checked={o.value === value}
              tabIndex={o.value === value ? 0 : -1}
              className={o.value === value ? 'is-on' : undefined}
              onClick={() => onChange(o.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') move(e, 1)
                else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') move(e, -1)
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
